import React from "react";
import ReactDOM from "react-dom";

import * as RadixMenu from "@radix-ui/react-dropdown-menu";
import cx from "classnames";
import { memoize, noop } from "lodash";
import { Node as ProseNode } from "prosemirror-model";
import {
	EditorState,
	Plugin,
	PluginKey,
	Selection as PMSelection,
	type SelectionBookmark,
	TextSelection,
	Transaction,
} from "prosemirror-state";
import { EditorView } from "prosemirror-view";

import { multiScore } from "~/lib/fzy";
import { hasOwnProperty } from "~/lib/js";
import { position } from "~/lib/prose/position";
import { safeAreaInsets } from "~/lib/safeareas";

import { Extension } from "../extension";

// 275px is approximately enough space for 5.5 items to show. Not too many, and
// a partial one can be displayed so it's obvious there are more (some devices
// won't show a scrollbar).
export const RESULTS_MAX_HEIGHT_PX = 275;

declare type Command = (state: EditorState, dispatch: (tr: Transaction) => void, view?: EditorView) => boolean;

export interface TypeAheadState {
	// The selection at the time of activation, so that the selection may
	// be restored at the end of the TypeAhead.
	selection?: SelectionBookmark | null;
	matchingSelection?: SelectionBookmark | null;
	// The DOM node that contains the type ahead UI.
	container: HTMLElement;
	// The DOM node that contains the type ahead results.
	resultContainer: HTMLElement;
	// The react ref of the displaying list.
	resultsRef: React.RefObject<TypeAheadListRef>;
	// Is the TypeAhead UI currently visible?
	isVisible: boolean;
	// The currently active TypeAheadProvider.
	provider: null | TypeAheadProvider;
	// The last position in the document at which a
	// TypeAhead was triggered.
	lastPos: number;
	// The results of the last search.
	results: TypeAheadResult[];
	text: {
		// The complete text of the node
		full: string;
		// The text to the left and right of the
		// cursor, ignoring leaf nodes and different
		// marks.
		adjacent: string;
		// The current word (delimited by " ") under
		// the cursor.
		word: string;
	};
	// An optional HTMLElement that will be used when positioning the TypeAhead UI.
	anchor?: HTMLElement;
	// Should the typeahead stay open when selection changes?
	persistent?: boolean;
	directionPreference?: "up" | "down";
}

export interface TypeAheadResult {
	id: number | string;
	title: string;
	content?: string;
	render?: (
		container: HTMLElement,
		view: EditorView,
		state: TypeAheadState | undefined,
		options: { [key: string]: unknown } | undefined,
	) => boolean;
	replaceMatch?: boolean;
	command: (state: EditorState, pluginState?: TypeAheadState) => Command | null;
	order?: (view: EditorView, state: TypeAheadState) => number;
	destroy?: (view: EditorView, state: TypeAheadState, container?: HTMLElement) => void;
	innerContainer?: HTMLElement;
}

export enum RegexStyle {
	WORD = "word",
	LINE = "line",
	ADJACENT = "adjacent",
}

export function resetState(state: TypeAheadState): TypeAheadState {
	// There was no match in the current editor state,
	// reset the plugin state.
	return {
		...state,
		text: {
			full: "",
			word: "",
			adjacent: "",
		},
		lastPos: -1,
		provider: null,
		isVisible: false,
		anchor: undefined,
		persistent: undefined,
		selection: null,
		matchingSelection: null,
	};
}

export abstract class TypeAheadProvider {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	options: { [key: string]: any };

	constructor(options = {}) {
		this.options = {
			...this.defaultOptions,
			...options,
		};
	}

	get direction(): "up" | "down" {
		return this.options.directionPreference || "up";
	}

	/**
	 * An optional hash of options that should be used as defaults when
	 * creating new instances of an extension.
	 */
	get defaultOptions() {
		return {
			style: "inline",
			mobileStyle: "inline",
			directionPreference: "up",
			clampPosition: true,
		};
	}

	/**
	 * The style of the input element that should be displayed when TypeAhead
	 * is triggered.
	 */
	get style(): string {
		return this.options.style;
	}

	get mobileHeight(): string {
		return "40%";
	}

	/**
	 * The style of the input element that should be displayed when TypeAhead
	 * is triggered on a mobile display.
	 */
	get mobileStyle(): string {
		return this.options.mobileStyle || this.options.style;
	}

	/**
	 * An optional CSS class that will be added to the TypeAhead container when
	 * it is triggered for this TypeAheadProvider.
	 */
	get popupClassName(): string {
		return "";
	}

	/**
	 * If a triggered TypeAheadProvider is persistent, it will not be closed or
	 * updated when the document changes. This is useful for TypeAheads opened
	 * via mouseclick or from a NodeView. Defaults to False.
	 */
	get persistent(): boolean {
		return false;
	}

	/**
	 * A simple utility method to "fuzzy" match the string `source` against `test`.
	 *
	 * Wraps around the (private) `fuzzyScore` method and handles multiple source
	 * strings in an array, getting a score for each element and returning the
	 * maximum.
	 *
	 * @param source - The string(s) to test against (ex: "Hello World"); if
	 * 	an array is provided, each string in the array will be tested, and the
	 * 	highest score will be returned.
	 * @param test - The string to test with (ex: "ello")
	 * @returns A score representing how close of a match there is
	 */
	fuzzy(source: string | readonly string[], test: string): number | undefined {
		return multiScore(source, test);
	}

	/**
	 * The keyboard key that should trigger this TypeAhead. If it returns null
	 * (the default) no keyboard trigger will be registered.
	 */
	get key(): string | null {
		return null;
	}

	/**
	 * A list of one or more regex that are compared to the text to see if this
	 * provider should be used.
	 *
	 * By default, this looks for a word starting with the same value as that
	 * return by `key()`, but can be overridden to test for multiple characters
	 * or any other pattern.
	 *
	 * This string this is tested against is modified by the value of
	 * `regexStyle()`.
	 *
	 * @returns The RegExp to test against.
	 */
	get regex(): RegExp[] {
		if (!this.key) return [];
		return [new RegExp(`(?:^)(?:${this.key})(\\S*)`)];
	}

	/**
	 * What context the regex should be run in. Can be either RegexStyle.WORD
	 * (the default) in which case the RegExp object returned by `regex()` will
	 * be tested against the current word under the cursor. Can also be
	 * RegexStyle.LINE in which case it will be tested the full  line of text
	 * under the cursor.
	 *
	 * @returns The style of regex to use.
	 */
	get regexStyle(): RegexStyle {
		return RegexStyle.WORD;
	}

	/**
	 * If this method returns True and the TypeAhead was triggered by a
	 * matching regex, the text that matched the regex will be removed from the
	 * document before the provider's applyResult is called.
	 */
	get replaceMatch() {
		return true;
	}

	/**
	 * Method called when the a click has occurred in the editor to see if this
	 * provider should be displayed.
	 *
	 * If the TypeAhead should be displayed this method should return the
	 * HTMLElement the menu should be positioned relative to.
	 *
	 * If this element is removed, the TypeAhead prompt will close.
	 *
	 * @return Element to use as anchor or null
	 */
	handleClickOn(
		_view: EditorView,
		_pos: number,
		_node: ProseNode,
		_nodePos: number,
		_event: MouseEvent,
		_direct: boolean,
	): { anchor?: HTMLElement; selection?: PMSelection } | null | boolean {
		return null;
	}

	/**
	 * Determines if the TypeAhead should automatically complete when a single
	 * result is returned. Defaults to False.
	 *
	 * @returns True if the result should be completed.
	 */
	complete() {
		return false;
	}

	/**
	 * Called to get the list of results to display in the TypeAhead when a
	 * regex from this TypeAheadProvider matched.
	 *
	 * When triggered explicitly, such as by a mouse click, the searchString
	 * will always be blank.
	 *
	 * @param view - The EditorView that this TypeAheadProvider is being
	 *				 triggered for.
	 * @param searchString - The matching string (if any).
	 * @returns A list of TypeAheadResult to be displayed to the user.
	 */
	abstract search(view: EditorView, searchString: string, state: TypeAheadState, plugin: TypeAhead): TypeAheadResult[];
}

export const TypeAheadPluginKey = new PluginKey<TypeAheadState>("typeahead");

interface TypeAheadChoiceProps {
	active?: boolean;
	choice: TypeAheadResult;
	baseClassName?: string;
	editorView: EditorView;
	state?: TypeAheadState;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	options?: { [key: string]: any };
	// FIXME: somehow there was a type error when using
	// HTMLProps<HTMLButtonElement>
	type?: "button" | "submit" | "reset";
	onClick: () => void;
	onMouseMove?: (e: React.MouseEvent) => void;
	useMenuItem?: boolean;
}

interface TypeAheadChoiceRef {
	click(): void;
	result: TypeAheadResult;
	dom: HTMLElement | null;
}

export const TypeAheadChoice = React.forwardRef<TypeAheadChoiceRef, TypeAheadChoiceProps>(function TypeAheadChoice(
	{
		useMenuItem,
		active,
		choice,
		baseClassName = "typeahead-result-item",
		editorView,
		state,
		options,
		onClick,
		onMouseMove,
		type = "button",
	},
	ref,
) {
	const buttonRef = React.useRef<HTMLButtonElement>(null);
	const innerRef = React.useRef<HTMLDivElement | null>(null);
	const resultRef = React.useRef(choice);

	const assignInnerRef = React.useCallback(
		(ref: HTMLDivElement | null) => {
			innerRef.current = ref;
			choice.innerContainer = ref ?? undefined;
		},
		[choice],
	);

	React.useEffect(() => void (resultRef.current = choice), [choice]);

	React.useImperativeHandle(ref, () => ({
		click() {
			buttonRef.current?.click();
		},
		get result() {
			return resultRef.current;
		},
		get dom() {
			return buttonRef.current;
		},
	}));

	React.useEffect(() => {
		if (!choice.render || !innerRef.current) return;
		// don't like
		ReactDOM.unmountComponentAtNode(innerRef.current);
		choice.render(innerRef.current, editorView, state, options);
	}, [choice, editorView, state, options]);

	const content = (
		<div ref={assignInnerRef} className={`${baseClassName}-inner`}>
			{!choice.render && (
				<div className={`${baseClassName}-content`}>
					<div className={`${baseClassName}-title`}>{choice.title}</div>
					<div className={`${baseClassName}-subtext`}>{choice.content}</div>
				</div>
			)}
		</div>
	);

	const className = cx(baseClassName, { [`${baseClassName}-active`]: active });
	if (useMenuItem) {
		return (
			<RadixMenu.Item asChild={true} onSelect={onClick}>
				<button ref={buttonRef} className={cx(className, "use-menu-item")} type={type} onMouseMove={onMouseMove}>
					{content}
				</button>
			</RadixMenu.Item>
		);
	} else {
		return (
			<button ref={buttonRef} type={type} className={className} onClick={onClick} onMouseMove={onMouseMove}>
				{content}
			</button>
		);
	}
});

interface TypeAheadListProps {
	choices: readonly TypeAheadResult[];
	onResultClick?: (result: TypeAheadResult) => void;
	editorView: EditorView;
	state?: TypeAheadState;
	baseClassName?: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	options?: { [key: string]: any };
	as?: React.ComponentType<{}>;
	useMenuItem?: boolean;
}

interface TypeAheadListRef {
	options: readonly (TypeAheadChoiceRef | null)[];
	active: string | number | null;
}

export const TypeAheadList = React.forwardRef<TypeAheadListRef, TypeAheadListProps>(function TypeAheadList(
	{ as, useMenuItem, baseClassName, choices, onResultClick, editorView, state, options },
	ref,
) {
	const [active, setActive] = React.useState<string | number | null>(null);
	const choiceRefs = React.useRef<(TypeAheadChoiceRef | null)[]>([]);
	const activeRef = React.useRef(active);
	const setActiveRef = React.useRef(setActive);

	React.useEffect(() => void (activeRef.current = active), [active]);
	React.useEffect(() => void (setActiveRef.current = setActive), [setActive]);

	const assignRef = React.useMemo(
		() =>
			memoize((index: number) => (r: TypeAheadChoiceRef | null) => {
				choiceRefs.current[index] = r;
			}),
		[choiceRefs],
	);

	React.useImperativeHandle(ref, () => ({
		get options() {
			return choiceRefs.current;
		},
		get active() {
			return activeRef.current;
		},
		set active(value) {
			setActiveRef.current(value);
		},
	}));

	const makeOnClick = React.useMemo(
		() =>
			memoize((result: TypeAheadResult) => () => {
				return onResultClick?.(result);
			}),
		[onResultClick],
	);

	const onMouseMove = React.useMemo(
		() =>
			memoize((id: string | number) => () => {
				setActive(id);
			}),
		[setActive],
	);

	const Container = as ?? "div";

	return (
		<Container>
			{choices.map((c, i) => (
				<TypeAheadChoice
					ref={assignRef(i)}
					key={c.id}
					active={c.id === active}
					choice={c}
					onClick={makeOnClick(c)}
					onMouseMove={onMouseMove(c.id)}
					editorView={editorView}
					state={state}
					options={options}
					baseClassName={baseClassName}
					useMenuItem={useMenuItem}
				/>
			))}
		</Container>
	);
});

export default class TypeAhead extends Extension {
	providers: TypeAheadProvider[];

	static MobileBreakPoint = 480;
	dimmer?: HTMLElement;

	get name() {
		return "typeahead";
	}

	constructor(options = {}) {
		super(options);
		this.providers = this.options.providers;
	}

	get defaultOptions() {
		return {
			// Style used for the full floating container.
			containerClass: "typeahead",
			// Style used for the result list container.
			resultContainerClass: "typeahead-results",
			// Style used for an individual result container.
			resultItemClass: "typeahead-result-item",
			// Default direction for menu will be down.
			directionPreference: "up",
			providers: [],
		};
	}

	handleClickOn = (
		view: EditorView,
		pos: number,
		node: ProseNode,
		nodePos: number,
		event: MouseEvent,
		direct: boolean,
	) => {
		const state = TypeAheadPluginKey.getState(view.state);
		if (!state) return false;

		for (const provider of this.providers) {
			const res = provider.handleClickOn(view, pos, node, nodePos, event, direct);
			if (res === true) {
				return true;
			}
			if (res) {
				const { anchor, selection } = res;
				if (anchor) {
					event.preventDefault();
					event.stopPropagation();
					const tempState = {
						...state,
						provider: provider,
						persistent: true,
						selection: (selection || view.state.selection).getBookmark(),
						anchor: anchor,
						isVisible: true,
						lastPos: pos,
					};
					view.dispatch(
						view.state.tr.setMeta(TypeAheadPluginKey, {
							...tempState,
							results: provider.search(view, "", tempState, this),
						}),
					);
					return true;
				}
			}
		}

		if (state && state.isVisible && state.persistent) {
			view.dispatch(view.state.tr.setMeta(TypeAheadPluginKey, resetState(state)));
		}

		return false;
	};

	/**
	 * Handle non-text keyboard input when the completion is visible.
	 *
	 * @param view - The view that generated the key event.
	 * @param event - The event triggered.
	 * @return True if event was handled, otherwise false.
	 */
	handleKeyDown = (view: EditorView, event: KeyboardEvent) => {
		const state = TypeAheadPluginKey.getState(view.state) as TypeAheadState;

		if (!state.isVisible) return false;

		switch (event.key) {
			case "ArrowUp":
			case "ArrowDown": {
				const nextChoice = this.nextChoice(state, event.key === "ArrowUp");
				if (nextChoice) return true;
				break;
			}
			case "Tab":
			case "Enter": {
				const currentChoice = this.currentChoice(state);
				if (currentChoice) {
					currentChoice[1].click();
					return true;
				}
				break;
			}
			case "Escape": {
				const { editorView } = this;
				if (editorView) {
					editorView.dispatch(editorView.state.tr.setMeta(TypeAheadPluginKey, resetState(state)));
					return true;
				}
				break;
			}
		}

		if (state && state.isVisible && state.persistent) {
			view.dispatch(view.state.tr.setMeta(TypeAheadPluginKey, resetState(state)));
		}

		return false;
	};

	/**
	 * Select the next valid choice in the list of results, updating the active
	 * styling.
	 *
	 * @param reverse - Select the previous choice instead of the next.
	 * @return True if the change could be made.
	 */
	nextChoice(state: TypeAheadState, reverse = false): boolean {
		if (!state.provider) return false;
		const ref = state.resultsRef.current;
		if (!ref?.options.length) return false;
		const { options } = ref;

		let newChoice = options[reverse ? options.length - 1 : 0];

		for (let i = 0; i < options.length; i++) {
			const el = options[i];
			const isActive = el?.result.id === ref.active;

			if (isActive) {
				const newIndex = i + (reverse ? -1 : 1);

				if (newIndex < 0) {
					newChoice = options[options.length - 1];
				} else if (newIndex >= options.length) {
					newChoice = options[0];
				} else {
					newChoice = options[newIndex];
				}
				break;
			}
		}
		if (newChoice) ref.active = newChoice.result.id;
		this.scrollCurrentChoiceIntoView(state, newChoice);
		return true;
	}

	scrollCurrentChoiceIntoView = (state: TypeAheadState, element: TypeAheadChoiceRef | null) => {
		if (state.container.scrollHeight <= state.container.clientHeight) return;
		const currentChoiceDOM = (() => {
			if (element) return element.dom;
			const currentChoice = this.currentChoice(state);
			if (!currentChoice) return null;
			return currentChoice[1].dom;
		})();
		if (!currentChoiceDOM) return;
		const endOffset = state.container.scrollTop + state.container.clientHeight;
		const startOffset = state.container.scrollTop;
		if (
			currentChoiceDOM.offsetTop + currentChoiceDOM.clientHeight - 0.1 * currentChoiceDOM.clientHeight > endOffset ||
			currentChoiceDOM.offsetTop < startOffset - 10
		) {
			state.container.scrollTo(0, currentChoiceDOM.offsetTop - 10);
		}
	};

	/**
	 * The currently selected TypeAheadResult from the list of choices.
	 *
	 * @return {TypeAheadResult} The currently selected result.
	 */
	currentChoice(state: TypeAheadState): [TypeAheadResult, TypeAheadChoiceRef] | null {
		if (!state.provider || !state.results.length) return null;
		const activeId = state.resultsRef.current?.active;
		const active = state.resultsRef.current?.options.find(o => o?.result.id === activeId);
		if (active) {
			for (const result of state.results) {
				// USE ==, we *want* this to cast.
				if (result.id == active.result.id) {
					return [result, active];
				}
			}
		}
		return null;
	}

	/**
	 * Calls the destructor for all of the results in the state.
	 *
	 * This is especially useful when including React-based components in the
	 * list of results to call ReactDOM.unmountComponentAtNode to prevent leaks.
	 *
	 * Normally called internally when hiding the TypeAhead or when updating the
	 * list of results. It is unlikely that you should need to call this method
	 * directly.
	 */
	cleanupResults = (state: Readonly<TypeAheadState>) => {
		const { editorView } = this;
		if (editorView) {
			for (let result of state.results) {
				if (result.destroy) result.destroy(editorView, state, result.innerContainer);
			}
		}
	};

	/**
	 * Populates the TypeAhead result container with the results of a
	 * TypeAheadProvider search.
	 */
	populateResults = (state: TypeAheadState) => {
		const { editorView, options } = this;
		if (!editorView) return;
		const { provider, results, resultContainer } = state;
		if (!provider) return;

		results.sort((a, b) => {
			if (!this.editorView) return 0;
			if (!a.order || !b.order) return 0;
			return a.order(editorView, state) < b.order(editorView, state) ? -1 : 1;
		});

		ReactDOM.render(
			<TypeAheadList
				ref={state.resultsRef}
				choices={results}
				editorView={editorView}
				state={state}
				options={options}
				baseClassName={options.resultItemClass}
				// eslint-disable-next-line react/jsx-no-bind
				onResultClick={result => {
					// If there's only a single result and the provider has autocomplete on,
					// we should just immediately make the change instead of displaying anything.
					const { matchingSelection } = state;
					// Big ol' hack, TypeAheadProvider's should have a
					// method to return just a modified transaction, which
					// command() could call as well.
					const editorState = editorView.state;
					const command = result.command(editorState, state);
					if (command) {
						const currentSel = editorState.tr.selection;
						command(
							editorState,
							(tr: Transaction) => {
								tr.setMeta(TypeAheadPluginKey, resetState(state));
								if (
									(hasOwnProperty(result, "replaceMatch") ? result.replaceMatch : provider.replaceMatch) &&
									matchingSelection
								) {
									const mappedSelection = matchingSelection.resolve(tr.doc);
									mappedSelection.replace(tr);
									tr.setSelection(currentSel.map(tr.doc, tr.mapping));
								}
								editorView.dispatch(tr);
							},
							editorView,
						);
					}
				}}
			/>,
			resultContainer,
			function afterResultsRendered() {
				const ref = state.resultsRef.current;
				if (!ref) return;

				ref.active = results[0].id;
			},
		);
	};

	private style(provider: TypeAheadProvider) {
		return window.innerWidth <= TypeAhead.MobileBreakPoint ? provider.mobileStyle : provider.style;
	}

	/**
	 * Removes any traces of the TypeAhead UI from the given state from the
	 * current document.
	 */
	hideContainer = (state: Readonly<TypeAheadState>, refocus = true) => {
		const { container, provider } = state;
		if (window.innerWidth <= TypeAhead.MobileBreakPoint && provider && this.style(provider).startsWith("fullscreen")) {
			container.style.maxHeight = "0%";
			// only clean up content once sliding out is complete
			container.ontransitionend = () => {
				this.cleanupResults(state);
				container.ontransitionend = noop;
			};
			if (this.dimmer) {
				this.dimmer.classList.remove("visible");
			}
		} else {
			container.remove();
			// Restore the selection since we lose focus when clicking outside the
			// entry.
			// Don't due this on mobile as it could cause the keyboard to show when
			// editing a due date
			if (this.editorView && refocus) this.editorView.focus();
			this.cleanupResults(state);
		}
	};

	/**
	 * Adds the TypeAhead UI for the given state to the current document.
	 */
	showContainer = (state: Readonly<TypeAheadState>) => {
		if (!this.editorView) {
			console.warn("showContainer called without an active Prose view");
			return;
		}
		const { container, provider } = state;
		if (!provider) {
			console.warn("showContainer called without an active TypeAheadProvider");
			return;
		}
		const style = this.style(provider);
		container.classList.add(style);
		if (provider.popupClassName) container.classList.add(provider.popupClassName);

		if (!this.editorView.dom.parentNode) return;
		if (style.startsWith("fullscreen")) {
			if (!this.dimmer) {
				this.dimmer = document.createElement("div");
				this.dimmer.classList.add("fullscreen-dimmer");
				this.dimmer.addEventListener("click", () => {
					if (this.editorView) {
						const newState = TypeAheadPluginKey.getState(this.editorView.state) as TypeAheadState;
						this.hideContainer(newState);
					}
				});
				document.body.appendChild(this.dimmer);
			}
			this.dimmer.classList.add("visible");
			if (document.activeElement && document.activeElement instanceof HTMLElement) {
				document.activeElement.blur(); // Prevent note from keeping focus
			}
			document.body.appendChild(state.container);
			const { editorView } = this;
			if (!editorView) return;
			editorView.dispatch(editorView.state.tr.scrollIntoView());
		} else {
			// eslint-disable-next-line no-lonely-if
			if (this.options.mountToDocument) {
				document.body.appendChild(state.container);
			} else {
				this.editorView.dom.parentNode.appendChild(state.container);
			}
		}
	};

	onVisualViewportChange = () => {
		if (!this.editorView) return;

		const state = TypeAheadPluginKey.getState(this.editorView.state);
		if (!state) return;

		this.updatePosition(state);
	};

	updatePosition = (state: Readonly<TypeAheadState>) => {
		const { editorView } = this;
		if (!editorView || !state.isVisible) return;
		const { container, provider } = state;
		if (!provider) return;

		const style = this.style(provider);

		container.classList.add(style);
		if (provider.popupClassName) container.classList.add(provider.popupClassName);

		if (style.startsWith("fullscreen")) {
			// Undo any offset that might have been add by inline styling.
			container.style.bottom = "";
			container.style.maxHeight = provider.mobileHeight;
			if (this.dimmer) {
				this.dimmer.classList.add("visible");
			}
			if (document.activeElement && document.activeElement instanceof HTMLElement) {
				document.activeElement.blur(); // Prevent note from keeping focus
			}
			return;
		}

		let anchor;
		if (!state.anchor) {
			anchor = editorView.coordsAtPos(state.lastPos);
		} else {
			anchor = state.anchor.getBoundingClientRect();
		}

		let parentBox;
		if (this.options.mountToDocument) {
			parentBox = document.documentElement.getBoundingClientRect();
		} else {
			parentBox = editorView.dom.getBoundingClientRect();
		}

		const renderedMenuBox = container.getBoundingClientRect();

		// Not available by default on Firefox <91, and not available at all on
		// Safari <13.
		const vv = window.visualViewport as VisualViewport | undefined;
		const padding = 8;

		let top, left, maxHeight: number | undefined;
		if (vv) {
			const topSpace = anchor.top - vv.offsetTop - safeAreaInsets.top;
			const bottomSpace = vv.offsetTop + vv.height - anchor.bottom - safeAreaInsets.bottom;

			// Put the menu on the side with more space.
			const isBeneath = bottomSpace > topSpace;
			const space = Math.max(bottomSpace, topSpace);
			// Padding between menu and anchor, and padding between menu and screen
			// edge.
			maxHeight = space - 2 * padding;
			// Technically this condition isn't enough to _actually_ know if the
			// navbar is there (for example in the browser extension it's not there
			// even though the viewport is narrow), but to do this properly would
			// take effort that would be better spent refactoring typeahead to use a
			// portal (so that it would just cover the navbar). In the name of saving
			// time, we make the "safe" choice of assuming that the navbar is there
			// taking up space.
			if (window.innerWidth <= TypeAhead.MobileBreakPoint && isBeneath) {
				// Height of navbar (safe area is already accounted for in bottomSpace)
				maxHeight -= 62;
			}
			const height = renderedMenuBox.height;

			// container is position absolute and parent is "position: relative", so
			// top/left are relative to that.
			top = isBeneath
				? anchor.bottom - parentBox.top + padding
				: anchor.top - parentBox.top - Math.min(maxHeight, height, RESULTS_MAX_HEIGHT_PX) - padding;

			left = Math.max(0, Math.min(anchor.left - parentBox.left, parentBox.width - renderedMenuBox.width));
		} else {
			let isBeneath;
			({ left, top, isBeneath } = position(renderedMenuBox, anchor, parentBox));
			top += isBeneath ? padding : -padding;
		}

		container.style.top = `${top}px`;
		container.style.left = `${left}px`;
		container.style.maxHeight = `${Math.min(maxHeight ?? Infinity, RESULTS_MAX_HEIGHT_PX)}px`;
	};

	scrollContainer?: HTMLElement;

	init() {
		window.visualViewport?.addEventListener("resize", this.onVisualViewportChange);
		window.visualViewport?.addEventListener("scroll", this.onVisualViewportChange);
		this.scrollContainer = this.editorView?.dom.closest(".scroll-container") ?? undefined;
		this.scrollContainer?.addEventListener("scroll", this.onVisualViewportChange);
	}

	destroy = () => {
		const { editorView } = this;
		if (!editorView) return;
		const state = TypeAheadPluginKey.getState(editorView.state) as TypeAheadState;
		if (state) this.hideContainer(state, false);

		window.visualViewport?.removeEventListener("resize", this.onVisualViewportChange);
		window.visualViewport?.removeEventListener("scroll", this.onVisualViewportChange);
		this.scrollContainer?.removeEventListener("scroll", this.onVisualViewportChange);
		delete this.scrollContainer;
	};

	get plugins() {
		const extension = this;
		return [
			new Plugin({
				appendTransaction: (_transactions: readonly Transaction[], _, newState: EditorState) => {
					const state = TypeAheadPluginKey.getState(newState) as TypeAheadState;
					if (!state) return;

					// If there's only a single result and the provider has autocomplete on,
					// we should just immediately make the change instead of displaying anything.
					const { provider, matchingSelection, results } = state;
					if (provider && state.isVisible && results.length === 1 && provider.complete()) {
						const result = results[0];
						// Big ol' hack, TypeAheadProvider's should have a
						// method to return just a modified transaction, which
						// command() could call as well.
						const command = result.command(newState, state);
						if (command) {
							let modifiedTr = undefined;
							const currentSel = newState.tr.selection;
							const success = command(newState, (tr: Transaction) => {
								tr.setMeta(TypeAheadPluginKey, resetState(state));
								if (
									(hasOwnProperty(result, "replaceMatch") ? result.replaceMatch : provider.replaceMatch) &&
									matchingSelection
								) {
									const mappedSelection = matchingSelection.resolve(tr.doc);
									mappedSelection.replace(tr);
									tr.setSelection(currentSel.map(tr.doc, tr.mapping));
								}
								modifiedTr = tr;
							});
							// If the command failed for some reason (ex: can't have a todo in a title)
							// make sure we hide the TypeAhead.
							if (!success) return newState.tr.setMeta(TypeAheadPluginKey, resetState(state));
							return modifiedTr;
						}
					}
					return undefined;
				},
				view: () => {
					return {
						update: (view: EditorView, prevEditorState: EditorState) => {
							// Called whenever the editorView has a new EditorState applied to it. We sync our DOM to
							// the state stored in the plugin.
							const state = TypeAheadPluginKey.getState(view.state) as TypeAheadState;
							if (!state) return;

							// Only hide the typeahead if it was previously visible.
							const prevState = TypeAheadPluginKey.getState(prevEditorState) as TypeAheadState;
							if (prevState && prevState.isVisible && !state.isVisible) {
								extension.hideContainer(state);
								return;
							}

							// The TypeAhead is attached to a DOM node that has been removed since the last transaction.
							if (state.persistent && state.isVisible && state.anchor && !state.anchor.closest("body")) {
								extension.hideContainer(state);
								return;
							}

							if (state.isVisible) {
								if (!state.container.parentElement) extension.showContainer(state);
								extension.updatePosition(state);
							}
						},
						destroy: () => {
							const { editorView } = extension;
							if (!editorView) return;
							const state = TypeAheadPluginKey.getState(editorView.state) as TypeAheadState;
							if (state) extension.hideContainer(state, false);
						},
					};
				},
				state: {
					init: (): TypeAheadState => {
						const container = document.createElement("div");
						container.classList.add(extension.options.containerClass);

						const resultContainer = document.createElement("div");
						resultContainer.classList.add(extension.options.resultContainerClass);

						container.appendChild(resultContainer);

						return {
							provider: null,
							lastPos: -1,
							text: {
								full: "",
								adjacent: "",
								word: "",
							},
							container: container,
							resultContainer: resultContainer,
							isVisible: false,
							results: [],
							resultsRef: React.createRef(),
						};
					},
					apply: (tr, pluginState: TypeAheadState, _prevState, state) => {
						// A click event or other plugin is explicitly setting
						// the Typeahead state.
						const meta = tr.getMeta(TypeAheadPluginKey);
						if (meta) {
							const newState = { ...pluginState, ...meta };
							if (newState.provider && newState.isVisible) {
								this.cleanupResults(pluginState);
								extension.populateResults(newState);
							}
							return newState;
						}

						// There's been a change in the structure of the document, map stored positions forward.
						if (tr.docChanged) {
							if (pluginState.matchingSelection) {
								pluginState.matchingSelection = pluginState.matchingSelection.map(tr.mapping);
							}
							if (pluginState.selection) {
								pluginState.selection = pluginState.selection.map(tr.mapping);
							}
							if (pluginState.lastPos != -1) {
								const res = tr.mapping.mapResult(pluginState.lastPos);
								// The position the TypeAhead was triggered at was delete, hide the prompt.
								// if (res.deleted) return resetState(pluginState);
								pluginState.lastPos = res.pos;
							}
						} else if (tr.selectionSet && pluginState.isVisible) {
							if (!pluginState.matchingSelection) return resetState(pluginState);
							const { from, to } = pluginState.matchingSelection.resolve(state.doc);
							if (state.selection.anchor < from || state.selection.anchor > to) return resetState(pluginState);
						} else {
							// If the document hasn't changed and the typeahead window isn't
							// visible, just do nothing.
							return pluginState;
						}

						if (pluginState.persistent || tr.getMeta("remoteChange")) return pluginState;

						const { selection } = state;
						const { $from } = selection;
						// Selection has a range, which we don't handle. Or the
						// selection has changed since the prompt was opened.
						if (selection.$anchor.pos !== selection.$head.pos) return resetState(pluginState);

						// Find the adjacent text (which will not cross leaf
						// boundaries such as hard_break) However, it does
						// consider new marks (bold into italic) as a different
						// node.
						let adjacentText = "";
						let offset = 0;
						const { nodeBefore, nodeAfter } = $from;
						if (nodeBefore && nodeBefore.text) {
							adjacentText += nodeBefore.text;
							offset += nodeBefore.text.length - 1;
						}
						if (nodeAfter && nodeAfter.text) {
							adjacentText += nodeAfter.text;
						}
						let wordStart = adjacentText.lastIndexOf(" ", offset);

						pluginState.text.adjacent = adjacentText;
						pluginState.text.full = $from.parent.textContent;
						pluginState.text.word = adjacentText.substring(wordStart + 1 /* +1 to exclude the space */);

						// No existing provider is active, check to see if any
						// match the current text.
						for (const tryProvider of this.providers) {
							for (const re of tryProvider.regex) {
								let result;
								let lastPos;
								switch (tryProvider.regexStyle) {
									case RegexStyle.WORD:
										result = pluginState.text.word.match(re);
										lastPos = $from.start() + $from.parentOffset;
										break;
									case RegexStyle.LINE:
										result = pluginState.text.full.match(re);
										lastPos = $from.start();
										wordStart = -1;
										break;
									case RegexStyle.ADJACENT:
										result = pluginState.text.adjacent.match(re);
										lastPos = $from.start();
										wordStart = -1;
										break;
								}
								if (result && extension.editorView) {
									const results = tryProvider.search(extension.editorView, result[1], pluginState, extension);
									if (results && results.length) {
										this.cleanupResults(pluginState);
										pluginState.provider = tryProvider;
										pluginState.lastPos = lastPos || -1;
										pluginState.results = results;
										pluginState.selection = selection.getBookmark();
										pluginState.anchor = undefined;

										// Create a selection bookmark that spans the entire matched text. Providers
										// can use this to replace the matching text with something new.
										pluginState.matchingSelection = TextSelection.create(
											state.doc,
											$from.pos - offset + wordStart,
											$from.pos - offset + wordStart + result[0].length,
										).getBookmark();

										extension.populateResults(pluginState);
										if (!pluginState.isVisible) pluginState.isVisible = true;
										return pluginState;
									}
								}
							}
						}

						return resetState(pluginState);
					},
				},
				props: {
					handleKeyDown: extension.handleKeyDown,
					handleClickOn: extension.handleClickOn,
				},
				key: TypeAheadPluginKey,
			}),
		];
	}
}
